import { FormattedMessage, injectIntl, type InjectedIntl } from "react-intl";
import ExclamationCircleIcon from "@heroicons/react/24/outline/ExclamationCircleIcon";
import CheckCircleIcon from "@heroicons/react/24/outline/CheckCircleIcon";

export type CaptchaUIState = "idle" | "loading" | "success";

export interface CaptchaNotificationProps {
  hasError: boolean;
  uiState: CaptchaUIState;
  onRetry?: () => void;
  intl: InjectedIntl;
}

const SpinnerIcon = () => (
  <svg
    className="sid-captcha-notification__spinner"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#9ae7bc" strokeWidth="1.5" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="#00a246" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CaptchaNotification = ({ hasError, uiState, onRetry, intl }: CaptchaNotificationProps) => {
  const submitButtonText = intl.formatMessage({
    id: "step.personalInfo.submitButtonText",
    defaultMessage: "Verify My Status",
  });

  if (uiState === "loading") {
    return (
      <div
        className="sid-captcha-notification sid-captcha-notification__loading"
        role="status"
        data-testid="captcha-loading"
      >
        <div className="sid-captcha-notification__icon-wrapper">
          <SpinnerIcon />
        </div>
        <div>
          <FormattedMessage
            id="step.personalInfo.captcha.loading"
            defaultMessage='Confirming you are human, please click "{submitButtonText}" again once the button becomes enabled.'
            values={{ submitButtonText }}
          />
        </div>
      </div>
    );
  }

  if (uiState === "success") {
    return (
      <div
        className="sid-captcha-notification sid-captcha-notification__success"
        role="status"
        data-testid="captcha-success"
      >
        <div className="sid-captcha-notification__icon-wrapper">
          <CheckCircleIcon className="sid-captcha-notification__icon-success" />
        </div>
        <div>
          <FormattedMessage
            id="step.personalInfo.captcha.success"
            defaultMessage='Confirmation complete, please click "{submitButtonText}" again to continue with verification.'
            values={{ submitButtonText }}
          />
        </div>
      </div>
    );
  }

  if (!hasError) {
    return null;
  }

  return (
    <div className="sid-captcha-notification" role="alert" data-testid="captcha-error">
      <div className="sid-captcha-notification__icon-wrapper">
        <ExclamationCircleIcon className="sid-captcha-notification__icon-error" />
      </div>
      <div>
        <FormattedMessage
          id="step.personalInfo.captcha.error"
          defaultMessage="Unable to confirm you are human. Please check your connection, disable ad-blockers, or refresh the page and try again."
        />
      </div>
      {onRetry && (
        <button type="button" className="sid-captcha-notification__retry-button" onClick={onRetry}>
          <FormattedMessage id="step.personalInfo.captcha.retryButton" />
        </button>
      )}
    </div>
  );
};

export const CaptchaNotificationComponent = injectIntl(CaptchaNotification);
